// Right-pane viewer for a diagram artifact (#11523): the Mermaid source
// rendered client-side by the chat's own DiagramViewer. Toolbar matches the
// FileViewer pane layout — close, download (the rendered SVG), an expand
// control for a full-screen view, and (when the diagram is a registry row
// the caller owns) a hand-edit mode over the Mermaid source.
import React, { useEffect, useRef, useState } from "react";
import { Copy, Download, PencilLine, Check } from "lucide-react";
import { saveBlobAs } from "./artifactDownload";
import { useLocation } from "react-router-dom";
import { useFlash } from "@/hooks/productTimerHooks";
import DiagramViewer from "../FpAppV4/DiagramViewer";
import ZoomControls from "../FpPages/RMS/Components/ZoomControls";
import { usePaneZoom } from "./usePaneZoom";
import { useViewerModeAction } from "../FpPages/ViewerModeToggle";
import { useArtifactPanelActions } from "./ArtifactPanelActions";
import ArtifactPaneHeader from "./ArtifactPaneHeader";
import ArtifactVersionHistoryPanel from "./ArtifactVersionHistoryPanel";
import { useArtifactHistory } from "./artifactHistory";
import { useInvalidateArtifactVersions } from "./artifactVersions";
import {
  ArtifactSourcesButton,
  artifactOriginLabel,
  useArtifactCitations,
} from "./ArtifactSources";

import {
  saveArtifactContent,
  type ArtifactVersion,
} from "../FpPages/Artifacts/useArtifacts";

interface DiagramPanelProps {
  diagram: string;
  title: string;
  /** Set when the diagram is an artifact — unlocks the header's action
   *  cluster. */
  artifactId?: string;
  /** False for a shared, not-mine artifact: the cluster stays, the
   *  hand-edit pencil stands down (r60b). Defaults true so chat dispatch
   *  sites, which only pass an id for one's own artifacts, keep it. */
  editable?: boolean;
  onClose: () => void;
}

const DiagramPanel: React.FC<DiagramPanelProps> = ({
  diagram,
  title,
  artifactId,
  editable = true,
  onClose,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // The pane owns the current source once mounted, so a saved hand-edit
  // renders without a reopen; a different diagram arriving resets it.
  const [source, setSource] = useState(diagram);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const { zoomLevel, zoomIn, zoomOut, resetZoom } = usePaneZoom();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When set, the pane shows this past version read-only — distinct from the
  // live source, so viewing history can never be mistaken for (or turn
  // into) a restore.
  const [viewingVersion, setViewingVersion] = useState<{
    versionNumber: number;
    source: string;
  } | null>(null);
  // What the diagram was built from (#11975), re-read as the source changes.
  const citations = useArtifactCitations(artifactId, source);
  const invalidateVersions = useInvalidateArtifactVersions();
  // The diagram the pane mounted with: a later one is a chat edit landing,
  // and a new version (#12481); the mount itself is not.
  const mountedDiagramRef = useRef(diagram);
  useEffect(() => {
    setSource(diagram);
    setEditing(false);
    setViewingVersion(null);
    setError(null);
    if (mountedDiagramRef.current !== diagram) {
      mountedDiagramRef.current = diagram;
      invalidateVersions(artifactId);
    }
  }, [diagram, artifactId, invalidateVersions]);

  // The rendered diagram is sanitized SVG already in the pane's DOM;
  // downloading is serializing that node — no server round-trip.
  const download = () => {
    const svg = contentRef.current?.querySelector("svg");
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    saveBlobAs(
      new Blob([serialized], { type: "image/svg+xml" }),
      `${title || "diagram"}.svg`
    );
  };

  const startEditing = () => {
    setDraft(source);
    setEditing(true);
  };

  const save = async () => {
    if (saving || !artifactId) return;
    setSaving(true);
    setError(null);
    try {
      await saveArtifactContent(artifactId, { text: draft });
      setSource(draft);
      setEditing(false);
      invalidateVersions(artifactId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // Version history presentation (#11819): the other header actions stand
  // down while it is open, and it renders as a column beside the diagram.
  const history = useArtifactHistory(artifactId);
  const { pathname } = useLocation();
  const [copied, flashCopied] = useFlash(2000);
  const shownSource = viewingVersion ? viewingVersion.source : source;
  const copy = () => {
    void navigator.clipboard.writeText(shownSource).then(() => flashCopied());
  };
  const { actions: artifactActions, dialogs: artifactDialogs } =
    useArtifactPanelActions({
      artifactId: artifactId ?? null,
      title,
      file: {
        key: "download",
        label: "Download",
        icon: Download,
        onSelect: download,
      },
      copy: {
        key: "copy",
        label: "Copy",
        hint: copied ? "Copied!" : "Copy source",
        icon: Copy,
        onSelect: copy,
      },
      edit:
        editable && !editing && !viewingVersion
          ? {
              key: "edit",
              label: "Edit source",
              icon: PencilLine,
              onSelect: startEditing,
            }
          : undefined,
    });
  const viewerModeAction = useViewerModeAction(true);
  // The history column's way of showing a version (#12481): a past
  // snapshot read-only, or the live source for null.
  const showVersion = (version: ArtifactVersion | null) => {
    setEditing(false);
    if (version === null) {
      setViewingVersion(null);
    } else if (version.content) {
      setViewingVersion({
        versionNumber: version.version_number,
        source: version.content,
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* One panel chrome for every artifact kind (#11548 r51/r60, #12481):
          the type tile and title (with the title's menu) on the left; the
          sources chip, the shared artifact actions, the layout toggle,
          Close on the right. The header folds as the pane narrows
          (#12091). */}
      <ArtifactPaneHeader
        title={title}
        badge={{ ext: "DIA", kind: "Diagram" }}
        origin={artifactOriginLabel(citations, pathname)}
        updatedAt={citations.updatedAt}
        version={
          artifactId
            ? {
                viewing: viewingVersion?.versionNumber ?? null,
                onView: showVersion,
                historyOpen: history.open,
                onToggleHistory: history.toggle,
              }
            : undefined
        }
        sources={
          !editing ? (
            <ArtifactSourcesButton
              sourceMap={citations.sourceMap}
              citationScope={citations.citationScope}
            />
          ) : undefined
        }
        onClose={onClose}
        leading={
          editing ? (
            <>
              {/* Editing gets explicit, labeled actions — a bare toolbar
                  icon was too easy to miss as the way to save. */}
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-[7px] border border-[var(--fp-border-strong)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--fp-ink)] hover:bg-[var(--fp-hover)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="flex items-center gap-1.5 rounded-[7px] bg-[var(--fp-ink)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--fp-surface)] hover:opacity-90 disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : undefined
        }
        aids={
          // Reading zoom (r74), in the header where it covers nothing
          // (#11819); the edit mode's split preview manages its own size.
          !editing && !history.open ? (
            <ZoomControls
              zoomLevel={zoomLevel}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onReset={resetZoom}
            />
          ) : undefined
        }
        actions={[
          ...artifactActions,
          ...(!history.open && viewerModeAction ? [viewerModeAction] : []),
        ]}
      >
        {artifactDialogs}
      </ArtifactPaneHeader>
      {error && (
        <div className="mx-4 mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        {editing ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden p-4">
            <textarea
              aria-label="Diagram source"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="h-2/5 w-full flex-none resize-none rounded-md border border-gray-200 p-3 font-mono text-[13px] leading-relaxed focus:border-gray-400 focus:outline-none"
            />
            {/* Live preview of the draft while editing. */}
            <div className="min-h-0 flex-1 overflow-auto">
              <DiagramViewer diagram={draft} title={title} unbounded />
            </div>
          </div>
        ) : (
          <div className="relative min-w-0 flex-1">
            <div
              ref={contentRef}
              className={`h-full overflow-auto p-4 fp-scroll-bare`}
            >
              <div style={{ zoom: zoomLevel / 100 }}>
                <DiagramViewer
                  diagram={viewingVersion ? viewingVersion.source : source}
                  title={title}
                  unbounded
                />
              </div>
            </div>
          </div>
        )}
        {history.open && artifactId && (
          <ArtifactVersionHistoryPanel
            artifactId={artifactId}
            viewing={viewingVersion?.versionNumber ?? null}
            onView={showVersion}
            onRestored={(version) => {
              if (version.content) {
                setEditing(false);
                setViewingVersion(null);
                setSource(version.content);
              }
            }}
            onError={setError}
          />
        )}
      </div>
    </div>
  );
};

export default DiagramPanel;
